import { Box, Button, ButtonGroup, Flex, Icon, Image } from '@homebotapp/hb-react-component-catalog'
import { defineMessages, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { useSendListingMessage } from '../../../hooks/listings/useSendListingMessage'
import { useEffect, useState } from 'react'
import {
  selectCustomerFirstName,
  selectCustomerHasSponsor,
  selectCustomerIsLoanOfficer,
  selectCustomerIsRealEstateAgent,
  selectCustomerPhotoUri
} from '../../../store/selectors/customerProfile'
import { MessageInput, MessageTopicKey } from '../../../api/gqlaxy/gql/generated/graphql'
import { selectSponsorshipFirstName, selectSponsorshipPhotoUri } from '../../../store/selectors/sponsorship'
import { selectZipcode } from '../../../store/selectors/home'
import * as Sentry from '@sentry/browser'
import { ConfettiTarget } from '../../shared/Confetti'
import { VerificationFlowProvider, VerificationFlowManager, useVerificationFlow } from '../../shared/VerificationFlow'
import { selectPreferredLoanOfficerMembership } from '../../../store/selectors/clientTeamMemberships'
import { useVerificationFlowEnabled } from '../../../hooks/useVerificationFlowEnabled'
import { CUSTOMER_MESSAGES } from '../../../constants/customerMessages'
import { useCustomerBranding } from '../../../hooks/homebotNetwork/usePreferredAgentOrCustomer'

const MSG = defineMessages({
  cancel: {
    id: 'AssumablesLearnMoreModal.cancel',
    defaultMessage: 'Cancel'
  },
  confirm: {
    id: 'AssumablesLearnMoreModal.confirm',
    defaultMessage: 'Confirm'
  },
  assumablesDm: {
    id: 'AssumablesLearnMoreModal.assumablesDm',
    defaultMessage: 'Ask {name} if you qualify'
  },
  assumablesDmGeneral: {
    id: 'AssumablesLearnMoreModal.assumablesDmGeneral',
    defaultMessage: 'Ask {name} about these listings'
  },
  success: {
    id: 'AssumablesLearnMoreModal.success',
    defaultMessage: 'Direct message sent!'
  },
  fail: {
    id: 'AssumablesLearnMoreModal.fail',
    defaultMessage: 'Direct message failed to send.'
  }
})

/**
 * DM animation is based off EstimatedMonthlyPayment
 *
 * Goal is to eventually move this whole thing to the DS
 */
enum DMStatus {
  Initial = 'initial',
  Confirmation = 'confirmation',
  Success = 'success',
  Fail = 'fail'
}

// this let's the confetti animation finish before resetting the state
const debounce = (func, delay) => {
  let timer
  return function (...args) {
    clearTimeout(timer)
    timer = setTimeout(() => {
      func.apply(this, args)
    }, delay)
  }
}

const animationTimer = 200

interface AssumablesDirectMessageProps {
  zipcode?: string
}

// Internal component that implements the direct message UI (without verification flow)
const AssumablesDirectMessageInternal = () => {
  const intl = useIntl()
  const { mutateAsync: sendListingMessage } = useSendListingMessage()

  const [isSending, setIsSending] = useState(false)
  const [dmStatus, setDmStatus] = useState<DMStatus>(DMStatus.Initial)
  const [showMiniConfetti, setShowMiniConfetti] = useState(false)
  const [showMiniConfettiAlt, setShowMiniConfettiAlt] = useState(false)
  const [confettiCounter, setConfettiCounter] = useState(0)

  const isCustomerLO = useSelector(selectCustomerIsLoanOfficer)
  const isCustomerREA = useSelector(selectCustomerIsRealEstateAgent)
  const isCustomerSponsored = useSelector(selectCustomerHasSponsor)

  const customerPhoto = useSelector(selectCustomerPhotoUri)
  const customerFirstName = useSelector(selectCustomerFirstName)

  const sponsorPhoto = useSelector(selectSponsorshipPhotoUri)
  const sponsorFirstName = useSelector(selectSponsorshipFirstName)

  const handleFinalStatus = () => {
    if (dmStatus === DMStatus.Fail) {
      setDmStatus(DMStatus.Initial)
    }

    if (dmStatus === DMStatus.Success) {
      setConfettiCounter(prevCounter => prevCounter + 1)
      const actionIndex = confettiCounter % 4

      switch (actionIndex) {
        case 0:
          setShowMiniConfetti(true)
          setShowMiniConfettiAlt(false)
          break
        case 1:
          setShowMiniConfettiAlt(true)
          break
        case 2:
          setShowMiniConfetti(true)
          break
        case 3:
          setShowMiniConfettiAlt(true)
          setTimeout(() => {
            if (confettiCounter % 4 === 3) {
              setShowMiniConfetti(false)
              setShowMiniConfettiAlt(false)
            }
          }, animationTimer)
          break
      }
    }
  }

  const debouncedHandleFinalStatus = debounce(handleFinalStatus, animationTimer)

  const handleSubmit = async () => {
    setIsSending(true)

    try {
      await sendListingMessage({
        input: {
          topicKey: MessageTopicKey.ListingsDirectMessageToSponsor,
          message: isCustomerREA
            ? 'Can you tell me more about this listing gallery?'
            : 'I want to explore assumable mortgages, is this available to me?',
          topic: 'Assumable Loans'
        }
      })
      setDmStatus(DMStatus.Success)
    } catch (error: any) {
      setDmStatus(DMStatus.Fail)

      Sentry.captureMessage('DM failed to send', {
        extra: {
          error: error.message || '',
          requestData: error.config?.data || {},
          requestErrors: error.response?.data?.errors ? Object.values(error.response.data.errors) : []
        }
      })
    } finally {
      setTimeout(() => {
        setIsSending(false)
      }, 300)
    }
  }

  return (
    <Flex direction='column' align={['unset', 'center']} maxW={['100%', '25rem']} w='100%'>
      {/* @ts-ignore */}
      <ConfettiTarget go={dmStatus === DMStatus.Success} numberOfPieces={40}>
        {/* @ts-ignore */}
        <ConfettiTarget go={showMiniConfetti} numberOfPieces={32}>
          {/* @ts-ignore */}
          <ConfettiTarget go={showMiniConfettiAlt} numberOfPieces={72}>
            <Box position={'relative'} mb={0} overflow={'hidden'} w={'100%'}>
              <Flex direction={'column'} gap={4} transition={'.15s ease-in-out transform'}>
                <Flex direction='column' gap={4} width='100%'>
                  {dmStatus === DMStatus.Initial && (
                    <Button
                      py='8px'
                      colorScheme='secondary'
                      width='100%'
                      isLoading={isSending}
                      variant={'outline'}
                      bg='neutral.50'
                      onClick={() => setDmStatus(DMStatus.Confirmation)}
                      tracking={{
                        guid: 'Assumables Learn More Modal',
                        descriptor: 'Initial Button Press to send message'
                      }}
                      leftIcon={
                        <Image
                          src={isCustomerSponsored ? sponsorPhoto : customerPhoto}
                          h={6}
                          w={6}
                          borderRadius='50%'
                        />
                      }
                    >
                      {isCustomerLO || isCustomerSponsored
                        ? intl.formatMessage(MSG.assumablesDm, {
                            name: isCustomerSponsored ? sponsorFirstName : customerFirstName
                          })
                        : intl.formatMessage(MSG.assumablesDmGeneral, { name: customerFirstName })}
                    </Button>
                  )}
                  {dmStatus === DMStatus.Confirmation && (
                    <ButtonGroup width={'100%'}>
                      {!isSending && (
                        <Button
                          variant={'outline'}
                          colorScheme='secondary'
                          bg='neutral.50'
                          w='auto'
                          transition={'.2s ease-in-out all'}
                          px={12}
                          tracking={{
                            guid: 'fHi5iCjZh1nsarWFHHPRf5',
                            ui_context: 'Assumables Learn More Modal',
                            descriptor: 'Cancel submission of dm'
                          }}
                          onClick={() => setDmStatus(DMStatus.Initial)}
                        >
                          {intl.formatMessage(MSG.cancel)}
                        </Button>
                      )}
                      <Button
                        w='100%'
                        onClick={() => handleSubmit()}
                        colorScheme='primary'
                        isLoading={isSending}
                        tracking={{
                          guid: 'sXCKvqYN7iQnoys7tbKGwL',
                          ui_context: 'Assumables Learn More Modal',
                          descriptor: 'Confirm direct message submission'
                        }}
                      >
                        {intl.formatMessage(MSG.confirm)}
                      </Button>
                    </ButtonGroup>
                  )}
                </Flex>
                {(dmStatus === DMStatus.Success || dmStatus === DMStatus.Fail) && (
                  <Button
                    w='100%'
                    variant='outline'
                    colorScheme='secondary'
                    bg='neutral.50'
                    mt={-4}
                    tracking={{
                      guid: '5TDGbTrJHZAL2jrUtSrRVi',
                      ui_context: 'Assumables direct message',
                      descriptor: 'Clicked on failure / success button'
                    }}
                    leftIcon={
                      dmStatus === DMStatus.Success ? <Icon name='check-circle-fa' width={3} height={3} /> : undefined
                    }
                    onClick={() => debouncedHandleFinalStatus()}
                  >
                    {dmStatus === DMStatus.Success && intl.formatMessage(MSG.success)}
                    {dmStatus === DMStatus.Fail && intl.formatMessage(MSG.fail)}
                  </Button>
                )}
              </Flex>
            </Box>
          </ConfettiTarget>
        </ConfettiTarget>
      </ConfettiTarget>
    </Flex>
  )
}

// Component that uses verification flow
const AssumablesDirectMessageWithVerification = ({ zipcode }: AssumablesDirectMessageProps) => {
  const intl = useIntl()
  const { state, startVerification, dispatch } = useVerificationFlow()
  const { mutateAsync: sendListingMessage } = useSendListingMessage()
  const [hasTriggeredSend, setHasTriggeredSend] = useState(false)

  const isCustomerREA = useSelector(selectCustomerIsRealEstateAgent)
  const stateZipcode = useSelector(selectZipcode)

  // Get branding - will show preferred loan officer if verified, otherwise customer/sponsor
  const branding = useCustomerBranding('LoanOfficer')
  const displayPhoto = branding?.photoUri
  const displayName = branding?.firstName

  // Watch for when verification completes and send the listing message
  useEffect(() => {
    const sendMessage = async () => {
      if (state.currentStep === 'messaging' && state.message && !hasTriggeredSend) {
        setHasTriggeredSend(true)

        const input: MessageInput = {
          topicKey: state.topicKey as MessageTopicKey,
          message: state.message,
          topic: 'Assumable Loans'
        }

        // Include metadata from verification flow (zipcode, galleryType)
        if (state.metadata?.zipcode) {
          input.metadata = { ...input.metadata, zipcode: state.metadata.zipcode }
        }
        if (state.metadata?.galleryType) {
          input.metadata = { ...input.metadata, galleryType: state.metadata.galleryType }
        }

        try {
          await sendListingMessage({ input })
          dispatch({ type: 'MESSAGE_SUCCESS' })
        } catch (error: any) {
          dispatch({ type: 'MESSAGE_FAILURE' })
          Sentry.captureMessage('Assumables DM failed to send', {
            extra: {
              error: error.message || '',
              requestData: error.config?.data || {},
              requestErrors: error.response?.data?.errors ? Object.values(error.response.data.errors) : []
            }
          })
        }
      }
    }

    sendMessage()
  }, [state.currentStep, state.message, state.topicKey, state.metadata, hasTriggeredSend, sendListingMessage, dispatch])

  // Reset hasTriggeredSend when moving back to idle state
  useEffect(() => {
    if (state.currentStep === 'idle') {
      setHasTriggeredSend(false)
    }
  }, [state.currentStep])

  const handleClick = () => {
    const message = isCustomerREA
      ? 'Can you tell me more about this listing gallery?'
      : 'I want to explore assumable mortgages, is this available to me?'

    const resolvedZipcode = zipcode || stateZipcode

    const metadata = {
      galleryType: 'assumables',
      ...(resolvedZipcode && { zipcode: resolvedZipcode })
    }

    startVerification(
      message,
      CUSTOMER_MESSAGES.HBN_FOR_YOU_GALLERY_LOAN_OFFICER.topicKey, // verified topic key: for_you_gallery_to_preferred_loan_officer
      CUSTOMER_MESSAGES.LISTINGS_DIRECT_MESSAGE_LO.topicKey, // default topic key: listings_direct_message
      'LoanOfficer',
      metadata
    )
  }

  return (
    <Flex direction='column' align={['unset', 'center']} maxW={['100%', '25rem']} w='100%'>
      <Button
        py='8px'
        colorScheme='secondary'
        width='100%'
        variant={'outline'}
        bg='neutral.50'
        onClick={handleClick}
        tracking={{
          guid: 'Assumables Learn More Modal',
          descriptor: 'Initial Button Press to send message with verification'
        }}
        leftIcon={<Image src={displayPhoto} h={6} w={6} borderRadius='50%' />}
      >
        {intl.formatMessage(MSG.assumablesDm, { name: displayName })}
      </Button>
    </Flex>
  )
}

// Wrapper component that provides verification flow context
export const AssumablesDirectMessage = ({ zipcode }: AssumablesDirectMessageProps) => {
  const loanOfficerMembership = useSelector(selectPreferredLoanOfficerMembership)
  const isVerificationFlowEnabled = useVerificationFlowEnabled()

  if (!isVerificationFlowEnabled) {
    // If verification flow is not enabled, render the internal component directly
    return <AssumablesDirectMessageInternal />
  }

  return (
    <VerificationFlowProvider
      options={{
        enableVerificationFlow: true,
        initialPartnerType: 'LoanOfficer',
        loanOfficerMembership: loanOfficerMembership,
        includePrequal: false
      }}
    >
      <VerificationFlowManager />
      <AssumablesDirectMessageWithVerification zipcode={zipcode} />
    </VerificationFlowProvider>
  )
}
